/**
 * Centralized axios instance for Member Clubhouse API calls.
 *
 * Migration notes:
 * - Auth is now via httpOnly cookie (`clubhouse_token`) set by /api/clubhouse/auth/login
 *   and /api/clubhouse/auth/register. Cookies are sent automatically when
 *   `withCredentials: true` is set on the request.
 * - CSRF protection uses the double-submit cookie pattern: the backend sets a
 *   non-httpOnly `clubhouse_csrf` cookie, and we echo it back via the
 *   `X-CSRF-Token` request header on POST/PUT/PATCH/DELETE.
 * - localStorage no longer holds the JWT. `clubhouse_user` (non-sensitive
 *   profile preview) may still be cached for instant UI rendering on app load.
 */
import axios from 'axios';
import { API_URL } from './apiUrl';

const STATE_CHANGING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

export const clubhouseApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Attach CSRF token on state-changing requests
clubhouseApi.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (STATE_CHANGING_METHODS.has(method)) {
    const csrf = readCookie('clubhouse_csrf');
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

/**
 * Bootstrap a CSRF cookie if not already present. Safe to call on app load.
 * Returns the CSRF token (may be useful for tests).
 */
export async function ensureCsrfToken() {
  if (readCookie('clubhouse_csrf')) return readCookie('clubhouse_csrf');
  try {
    const { data } = await clubhouseApi.get('/api/clubhouse/auth/csrf');
    return data?.csrf_token || readCookie('clubhouse_csrf');
  } catch {
    return null;
  }
}

/**
 * Log the current member out — clears auth + CSRF cookies on the server.
 * Also clears the cached `clubhouse_user` from localStorage.
 */
export async function logoutClubhouse() {
  try {
    await clubhouseApi.post('/api/clubhouse/auth/logout');
  } catch {
    // ignore — even if logout fails on the server, we still clear client state
  }
  try {
    localStorage.removeItem('clubhouse_user');
    localStorage.removeItem('clubhouse_token'); // legacy cleanup
  } catch {
    // localStorage not available (SSR / private mode)
  }
}

export default clubhouseApi;
